<template>
  <FlexCol class="relative" gap-1 ref="triggerRef">
    <Label v-if="label" :label="label" :required="required" :error="error" />
    <Flex gap-1 justifyBetween>
      <InputArc :disabled="disabled" :error="error" class="w-full">
        <template #pre-icon>
          <IconInput
            icon="fa-calendar-days"
            class="border-r"
            :disabled="disabled"
            @click="toggle"
          />
        </template>
        <template #input>
          <input
            v-model="dateBr"
            :disabled="disabled"
            placeholder="Data"
            class="w-full bg-transparent focus:outline-none focus:ring-0 placeholder:italic"
            maxlength="10"
          />
        </template>
      </InputArc>

      <InputArc :disabled="disabled" :error="error" class="w-full">
        <template #pre-icon>
          <IconInput icon="fa-clock" class="border-r" :disabled="disabled" />
        </template>
        <template #input>
          <input
            v-model="time"
            :disabled="disabled"
            placeholder="Hora"
            class="w-full bg-transparent focus:outline-none focus:ring-0 placeholder:italic"
            maxlength="5"
          />
        </template>
      </InputArc>
    </Flex>

    <Error v-if="error" :message="error" />

    <Teleport to="body">
      <div v-if="open" :style="popupStyle" class="fixed z-[2000]">
        <Calendar v-model="calendarDate" />
      </div>
    </Teleport>
  </FlexCol>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import moment from "moment";

import { useFormat } from "@/composables/useFormat";
const { formatDate, formatIsoDatetime, formatBrDateToIso } = useFormat();

const props = defineProps({
  label: String,
  required: Boolean,
  disabled: Boolean,
  error: [String, Array, Object],
  modelValue: [String, Date],
});

const emits = defineEmits(["update:modelValue"]);

const open = ref(false);
const syncing = ref(false);
const triggerRef = ref(null);
const popupStyle = ref({});

// Inputs isolados
// const localValue = ref(props.modelValue ? formatDate(props.modelValue) : null)
const dateBr = ref(props.modelValue ? formatDate(props.modelValue) : null); // DD/MM/YYYY
const time = ref("00:00"); // HH:mm

// Calendar trabalha só com Date
const calendarDate = ref(null);

const toggle = () => {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    nextTick(updatePopupPosition);
  }
};

const close = () => {
  open.value = false;
};

const normalizeTime = (value) => {
  const [hh = 0, mm = 0] = value.split(":").map(Number);
  if (hh > 23 || mm > 59) return "00:00";
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

const updatePopupPosition = () => {
  const triggerElement = triggerRef.value?.$el ?? triggerRef.value;
  if (!open.value || !triggerElement || typeof triggerElement.getBoundingClientRect !== "function") return;
  const rect = triggerElement.getBoundingClientRect();
  popupStyle.value = {
    top: `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  };
};

const emitValue = () => {
  if (!dateBr.value) return;

  const fixedTime = normalizeTime(time.value || "00:00");
  time.value = fixedTime;

  const [day, month, year] = dateBr.value.split("/");
  let iso = moment(`${year}-${month}-${day} ${fixedTime}:00`);

  iso = iso.format();

  // <<< trava anti-loop
  if (props.modelValue && moment(props.modelValue).isSame(iso)) return;

  emits("update:modelValue", iso);
};

//
// 🔁 modelValue → inputs
//
watch(
  () => props.modelValue,
  async (val) => {
    if (!val) return;

    syncing.value = true;

    const m = moment(val);

    dateBr.value = m.format("DD/MM/YYYY");
    time.value = m.format("HH:mm");
    calendarDate.value = m.startOf("day").toDate();

    await nextTick();
    syncing.value = false;
  },
  { immediate: true },
);

//
// 📅 Calendar → date
//
watch(calendarDate, (val) => {
  if (!val || syncing.value) return;

  syncing.value = true;
  dateBr.value = moment(val).format("DD/MM/YYYY");
  syncing.value = false;

  emitValue();
  close();
});

watch(open, (isOpen) => {
  if (isOpen) {
    nextTick(updatePopupPosition);
  }
});

//
// ⌨️ Hora digitada
//
watch(time, (val) => {
  if (syncing.value) return;

  if (val && val.length == 4 && !val.includes(":")) {
    time.value = `${val.slice(0, 2)}:${val.slice(2, 4)}`;
  }

  // deixa digitar livremente
  if (!val || val.length < 5) return;

  // agora sim normaliza
  const fixed = normalizeTime(val);
  if (fixed !== val) {
    syncing.value = true;
    time.value = fixed;
    syncing.value = false;
  }

  emitValue(); // true indica que foi digitado pelo usuário
});

//
// ⌨️ Data digitada
//
watch(dateBr, () => {
  if (syncing.value) return;

  syncing.value = true;
  if (dateBr.value.length == 8) {
    dateBr.value = moment(dateBr.value, "DDMMYYYY").format("DD/MM/YYYY");
  }

  if (dateBr.value.length < 10) {
    syncing.value = false;
    return;
  }

  if(!moment(dateBr.value, "DD/MM/YYYY", true).isValid()) {
    dateBr.value = "";
    syncing.value = false;
    return;
  }

  syncing.value = false;

  emitValue();
});

onMounted(() => {
  window.addEventListener("resize", updatePopupPosition);
  window.addEventListener("scroll", updatePopupPosition, true);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updatePopupPosition);
  window.removeEventListener("scroll", updatePopupPosition, true);
});
</script>
